/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

'use client'

import type { ChangeEvent, ChangeEventHandler, ComponentProps } from 'react'
import { forwardRef, useId } from 'react'
import type { RegisterOptions } from 'react-hook-form'
import { useFormContext, useFormState } from 'react-hook-form'

import { enumToString } from '../../../lib/enum-to-string'
import { cn, mergeRefs } from '../../../lib/utils'
import { Field, FieldDescription, FieldError, FieldLabel } from '../../ui/field'
import { NativeSelect } from '../../ui/native-select'

type EnumLike = Record<string, string | number>

export type EnumSelectFormFieldProps<TEnum extends EnumLike = EnumLike> = {
  autoComplete?: ComponentProps<'select'>['autoComplete']
  disabled?: boolean
  enumType?: TEnum
  fieldProps?: ComponentProps<typeof Field>
  helperText?: string
  label?: string
  name: string
  onValueChange?: ChangeEventHandler<HTMLSelectElement>
  placeholder?: string
  required?: boolean
  selectProps?: Omit<ComponentProps<typeof NativeSelect>, 'size'>
  validation?: RegisterOptions
}

export const EnumSelectFormField = forwardRef<HTMLSelectElement, EnumSelectFormFieldProps>((props, forwardedRef) => {
  const { register, control } = useFormContext()
  const { errors } = useFormState({ control, name: props.name })

  const { ref, ...workRegister } = register(props.name, { ...props.validation })

  const enumList = Object.keys(props.enumType ?? {})
    .filter((entry): entry is string => Number.isNaN(Number(entry)))
    .sort((a, b) => a.localeCompare(b))

  const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    workRegister.onChange(event)
    props?.onValueChange?.(event)
  }

  const error = errors[props.name]
  const invalid = !!error

  const generatedId = useId()
  const controlId = props.selectProps?.id ?? props.fieldProps?.id ?? generatedId

  const { selectProps } = props
  const { ref: selectRef, className: selectClassName, id: _selectId, ...restSelectProps } = selectProps ?? {}

  return (
    <Field {...props.fieldProps} data-invalid={invalid}>
      {props.label !== undefined && (
        <FieldLabel className="flex items-center gap-1" htmlFor={controlId}>
          {props.label}
          {props.required ? (
            <span aria-hidden className="text-destructive">
              *
            </span>
          ) : null}
        </FieldLabel>
      )}
      <NativeSelect
        {...restSelectProps}
        {...workRegister}
        aria-invalid={invalid}
        autoComplete={props.autoComplete}
        className={cn('w-full', selectClassName)}
        disabled={props.disabled}
        id={controlId}
        onChange={handleChange}
        ref={mergeRefs(ref, forwardedRef, selectRef)}
        required={props.required}
        suppressHydrationWarning
      >
        {props.placeholder !== undefined ? <option value="">{props.placeholder}</option> : null}
        {enumList.map((value) => (
          <option key={value} value={value}>
            {enumToString(value)}
          </option>
        ))}
      </NativeSelect>
      {props.helperText !== undefined ? <FieldDescription>{props.helperText}</FieldDescription> : null}
      {error?.message ? <FieldError>{String(error.message)}</FieldError> : null}
    </Field>
  )
})

EnumSelectFormField.displayName = 'EnumSelectFormField'
